import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useConvex } from "convex/react";
import { LocalMediaFile } from "../types";
import { getUserId } from "../utils/general/user-id";
import {
  getUserMediaItems,
  deleteMediaItem as deleteFromIndexDB,
  clearUserMedia,
  addMediaItem,
  UserMediaItem,
  getMediaItem,
} from "../utils/general/indexdb";
import { uploadMediaFile, deleteMediaFile } from "../utils/general/media-upload";
import { ConvexMediaService } from "../services/convex-media-service";
import { MediaUrlUpdateService } from "../services/media-url-update-service";

interface LocalMediaContextType {
  localMediaFiles: LocalMediaFile[];
  addMediaFile: (file: File) => Promise<LocalMediaFile | void>;
  removeMediaFile: (id: string) => Promise<void>;
  clearMediaFiles: () => Promise<void>;
  refreshMediaFiles: () => Promise<void>;
  isLoading: boolean;
  syncStatus: 'idle' | 'syncing' | 'completed' | 'error';
  syncProgress: { current: number; total: number };
  syncError: string | null;
  lastSyncTime: number | null;
  syncFromConvex: () => Promise<void>;
}

const LocalMediaContext = createContext<LocalMediaContextType | undefined>(
  undefined
);

/**
 * LocalMediaProvider Component
 *
 * Provides context for managing local media files uploaded by the user.
 * Handles:
 * - Storing and retrieving local media files from IndexedDB and server
 * - Adding new media files
 * - Removing media files
 * - Persisting media files between sessions
 */
export const LocalMediaProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [localMediaFiles, setLocalMediaFiles] = useState<LocalMediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userId] = useState(() => getUserId());
  const convexClient = useConvex(); // Get authenticated Convex client

  // Sync state management
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'completed' | 'error'>('idle');
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<number | null>(null);

  // Shared function to load media files from IndexedDB
  const loadMediaFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const mediaItems = await getUserMediaItems(userId);

      // Convert IndexedDB items to LocalMediaFile format
      // PREFER CONVEX URL OVER BLOB URL
      const files: LocalMediaFile[] = mediaItems.map((item) => {
        let itemPath: string;

        // Priority 1: Use Convex URL if available (best for rendering)
        if (item.convexUrl) {
          itemPath = item.convexUrl;
          console.log(`[LocalMedia] Using Convex URL for ${item.name}`);
        }
        // Priority 2: Use existing serverPath (keeps blob URLs consistent)
        // IMPORTANT: Don't create fresh blob URLs - use the original one so URLs match in overlays
        else {
          itemPath = item.serverPath;
          if (item.serverPath.startsWith('blob:')) {
            console.warn(`[LocalMedia] Using blob URL for ${item.name} (upload status: ${item.uploadStatus || 'unknown'})`);
          } else {
            console.log(`[LocalMedia] Using server path for ${item.name}`);
          }
        }

        return {
          id: item.id,
          name: item.name,
          type: item.type,
          path: itemPath,
          size: item.size,
          lastModified: item.lastModified,
          thumbnail: item.thumbnail || "",
          duration: item.duration || 0,
        };
      });

      setLocalMediaFiles(files);
    } catch (error) {
      console.error("Error loading media files from IndexedDB:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Sync media files from Convex to IndexedDB
   * Fetches all files from Convex and adds missing ones to IndexedDB (metadata only)
   */
  const syncFromConvex = useCallback(async () => {
    if (!convexClient) {
      console.warn('[LocalMedia] No Convex client available for sync');
      return;
    }

    try {
      setSyncStatus('syncing');
      setSyncError(null);
      console.log('[LocalMedia] Starting sync from Convex...');

      // Step 1: Fetch all files from Convex
      const convexFiles = await ConvexMediaService.fetchUserFiles(convexClient);
      console.log(`[LocalMedia] Found ${convexFiles.length} files in Convex`);

      // Step 2: Get all files from IndexedDB
      const localFiles = await getUserMediaItems(userId);
      const localFileIds = new Set(localFiles.map(f => f.convexStorageId).filter(Boolean));
      console.log(`[LocalMedia] Found ${localFiles.length} files in IndexedDB`);

      // Step 3: Identify files that exist in Convex but not in IndexedDB
      const missingFiles = convexFiles.filter(
        convexFile => !localFileIds.has(convexFile.storageId)
      );
      console.log(`[LocalMedia] ${missingFiles.length} files need to be synced`);

      if (missingFiles.length === 0) {
        setSyncStatus('completed');
        setLastSyncTime(Date.now());
        console.log('[LocalMedia] Sync complete - no new files');
        return;
      }

      setSyncProgress({ current: 0, total: missingFiles.length });

      // Step 4: Batch fetch all URLs in a single query
      const storageIds = missingFiles.map(f => f.storageId);
      const urlMap = await ConvexMediaService.getFileUrlsBatch(convexClient, storageIds);

      // Step 5: Add missing files to IndexedDB in parallel
      let syncedCount = 0;
      const results = await Promise.allSettled(
        missingFiles.map(async (convexFile) => {
          const downloadUrl = urlMap.get(convexFile.storageId) ?? undefined;

          // Create IndexedDB entry with metadata + URL (no blob)
          const mediaItem: UserMediaItem = {
            id: convexFile._id,                    // Use Convex _id as IndexedDB id
            userId: userId,
            name: convexFile.fileName,
            type: convexFile.fileType,
            serverPath: downloadUrl || '',          // Use Convex URL
            size: convexFile.fileSize,
            lastModified: convexFile.lastModified,
            thumbnail: convexFile.thumbnail || '',
            duration: convexFile.duration || 0,
            createdAt: convexFile.createdAt,

            // Convex integration fields
            convexStorageId: convexFile.storageId,
            convexUrl: downloadUrl,
            fileHash: convexFile.fileHash,
            uploadStatus: 'synced',                // Mark as synced from Convex
            lastSyncedAt: Date.now(),

            // No fileBlob - metadata only
          };

          await addMediaItem(mediaItem);
          syncedCount++;
          setSyncProgress({ current: syncedCount, total: missingFiles.length });
          console.log(`[LocalMedia] Synced: ${convexFile.fileName}`);
        })
      );

      // Log any failures
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        console.error(`[LocalMedia] Failed to sync ${failures.length} file(s):`,
          failures.map(r => (r as PromiseRejectedResult).reason));
      }

      // Step 5: Reload media files to update UI
      await loadMediaFiles();

      setSyncStatus('completed');
      setLastSyncTime(Date.now());
      console.log(`[LocalMedia] Sync complete - synced ${missingFiles.length} files`);

    } catch (error) {
      console.error('[LocalMedia] Sync failed:', error);
      setSyncError(error instanceof Error ? error.message : 'Unknown sync error');
      setSyncStatus('error');
    }
  }, [convexClient, userId, loadMediaFiles]);

  // Load saved media files from IndexedDB on component mount
  useEffect(() => {
    if (userId && convexClient) {
      // Load from IndexedDB first (fast)
      loadMediaFiles();

      // Then sync from Convex in background
      syncFromConvex();
    }
  }, [userId, convexClient, loadMediaFiles, syncFromConvex]);

  /**
   * Add a new media file to the collection
   */
  const addMediaFile = useCallback(
    async (file: File): Promise<LocalMediaFile | void> => {
      setIsLoading(true);
      try {
        // Upload file with hybrid approach (IndexedDB + Convex upload)
        const mediaItem = await uploadMediaFile(file, convexClient);

        // Convert to LocalMediaFile format
        const newMediaFile: LocalMediaFile = {
          id: mediaItem.id,
          name: mediaItem.name,
          type: mediaItem.type,
          path: mediaItem.serverPath,
          size: mediaItem.size,
          lastModified: mediaItem.lastModified,
          thumbnail: mediaItem.thumbnail || "",
          duration: mediaItem.duration || 0,
        };

        // Update state with the new media file
        setLocalMediaFiles((prev) => {
          // Check if file with same ID already exists
          const exists = prev.some((item) => item.id === newMediaFile.id);
          if (exists) {
            // Replace existing file
            return prev.map((item) =>
              item.id === newMediaFile.id ? newMediaFile : item
            );
          }
          // Add new file
          return [...prev, newMediaFile];
        });

        return newMediaFile;
      } catch (error) {
        console.error("Error adding media file:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [convexClient]
  );

  /**
   * Remove a media file by ID
   */
  const removeMediaFile = useCallback(
    async (id: string): Promise<void> => {
      try {
        const fileToRemove = localMediaFiles.find((file) => file.id === id);

        if (fileToRemove) {
          // Delete from Convex first (if synced)
          const mediaItem = await getMediaItem(id);
          if (convexClient && mediaItem?.convexStorageId) {
            try {
              // Use the file's ID (which is the Convex document ID when synced)
              await ConvexMediaService.deleteFile(convexClient, id);
              console.log(`[LocalMedia] Deleted from Convex: ${fileToRemove.name}`);
            } catch (convexError) {
              console.error(`[LocalMedia] Failed to delete from Convex:`, convexError);
              // Continue with local deletion even if Convex fails
            }
          }

          // Delete from server (legacy)
          await deleteMediaFile(userId, fileToRemove.path);

          // Delete from IndexedDB
          await deleteFromIndexDB(id);

          // Update state
          setLocalMediaFiles((prev) => prev.filter((file) => file.id !== id));
        }
      } catch (error) {
        console.error("Error removing media file:", error);
      }
    },
    [localMediaFiles, userId, convexClient]
  );

  /**
   * Clear all media files
   */
  const clearMediaFiles = useCallback(async (): Promise<void> => {
    try {
      // Delete all files from server
      for (const file of localMediaFiles) {
        await deleteMediaFile(userId, file.path);
      }

      // Clear IndexedDB
      await clearUserMedia(userId);

      // Update state
      setLocalMediaFiles([]);
    } catch (error) {
      console.error("Error clearing media files:", error);
    }
  }, [localMediaFiles, userId]);

  // Subscribe to upload completion events to refresh media files
  useEffect(() => {
    const unsubscribe = MediaUrlUpdateService.subscribe(() => {
      loadMediaFiles();
    });
    return () => unsubscribe();
  }, [loadMediaFiles]);

  const value = {
    localMediaFiles,
    addMediaFile,
    removeMediaFile,
    clearMediaFiles,
    refreshMediaFiles: loadMediaFiles,
    isLoading,
    syncStatus,
    syncProgress,
    syncError,
    lastSyncTime,
    syncFromConvex,
  };

  return (
    <LocalMediaContext.Provider value={value}>
      {children}
    </LocalMediaContext.Provider>
  );
};

/**
 * Hook to use the local media context
 */
export const useLocalMedia = () => {
  const context = useContext(LocalMediaContext);
  if (context === undefined) {
    throw new Error("useLocalMedia must be used within a LocalMediaProvider");
  }
  return context;
};

// Alias for consistency with plan
export const useLocalMediaContext = useLocalMedia;
